import type { ParseKeys } from "i18next";
import { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "./i18n";

export type TourStep =
  | "className"
  | "classRole"
  | "classSubmit"
  | "rosterLink"
  | "rosterInput"
  | "rosterSubmit"
  | "seatingLink"
  | "seatingNav"
  | "seatingStudent"
  | "seatingSeat"
  | "seatingSave";

export type DemoTourStep =
  | "demoWelcome"
  | "demoToday"
  | "demoPerformance"
  | "demoCoursework"
  | "demoGroups"
  | "demoSeating"
  | "demoCalendar"
  | "demoAttendance"
  | "demoHomeroomHomework"
  | "demoStudentRecords"
  | "demoHomeroom"
  | "demoHomeroomDuties"
  | "demoToolSettings"
  | "demoRoster"
  | "demoData";

type TourItem = {
  target: string;
  navigationTarget?: string;
  chapter: number;
  title: ParseKeys<"guidedTour">;
  message: ParseKeys<"guidedTour">;
  next?: ParseKeys<"guidedTour">;
  needsValue?: boolean;
};

const steps: Record<TourStep, TourItem> = {
  className: {
    target: '[data-tour="class-name"]',
    chapter: 1,
    title: "steps.className.title",
    message: "steps.className.message",
    next: "steps.className.next",
    needsValue: true,
  },
  classRole: {
    target: '[data-tour="class-role"]',
    chapter: 1,
    title: "steps.classRole.title",
    message: "steps.classRole.message",
    next: "steps.classRole.next",
  },
  classSubmit: {
    target: '[data-tour="class-submit"]',
    chapter: 1,
    title: "steps.classSubmit.title",
    message: "steps.classSubmit.message",
  },
  rosterLink: {
    target: '[data-tour="roster-link"]',
    chapter: 2,
    title: "steps.rosterLink.title",
    message: "steps.rosterLink.message",
  },
  rosterInput: {
    target: '[data-tour="roster-input"]',
    chapter: 2,
    title: "steps.rosterInput.title",
    message: "steps.rosterInput.message",
    next: "steps.rosterInput.next",
    needsValue: true,
  },
  rosterSubmit: {
    target: '[data-tour="roster-submit"]',
    chapter: 2,
    title: "steps.rosterSubmit.title",
    message: "steps.rosterSubmit.message",
  },
  seatingLink: {
    target: '[data-tour="seating-link"]',
    chapter: 3,
    title: "steps.seatingLink.title",
    message: "steps.seatingLink.message",
  },
  seatingNav: {
    target: '[data-tour="seating-nav"]',
    chapter: 3,
    title: "steps.seatingNav.title",
    message: "steps.seatingNav.message",
    next: "steps.seatingNav.next",
  },
  seatingStudent: {
    target: '[data-tour="seating-student"]',
    chapter: 3,
    title: "steps.seatingStudent.title",
    message: "steps.seatingStudent.message",
  },
  seatingSeat: {
    target: '[data-tour="seating-seat"]',
    chapter: 3,
    title: "steps.seatingSeat.title",
    message: "steps.seatingSeat.message",
  },
  seatingSave: {
    target: '[data-tour="seating-save"]',
    chapter: 3,
    title: "steps.seatingSave.title",
    message: "steps.seatingSave.message",
  },
};

// Keep this sequence in feature order. TeacherConsole derives navigation from the
// same IDs, so adding a new user-facing tool requires adding its tour entry too.
export const DEMO_TOUR_SEQUENCE: DemoTourStep[] = [
  "demoWelcome",
  "demoToday",
  "demoPerformance",
  "demoCoursework",
  "demoGroups",
  "demoSeating",
  "demoCalendar",
  "demoAttendance",
  "demoHomeroomHomework",
  "demoStudentRecords",
  "demoHomeroom",
  "demoHomeroomDuties",
  "demoToolSettings",
  "demoRoster",
  "demoData",
];

const demoSteps: Record<DemoTourStep, Omit<TourItem, "chapter">> = {
  demoWelcome: {
    target: '[data-demo-tour="class-picker"]',
    title: "steps.demoWelcome.title",
    message: "steps.demoWelcome.message",
    next: "steps.demoWelcome.next",
  },
  demoToday: {
    navigationTarget: '[data-demo-nav="today"]',
    target: '[data-demo-tour-panel="today"]',
    title: "steps.demoToday.title",
    message: "steps.demoToday.message",
    next: "steps.demoToday.next",
  },
  demoPerformance: {
    navigationTarget: '[data-demo-nav="records-performance"]',
    target: '[data-demo-tour-panel="records"]',
    title: "steps.demoPerformance.title",
    message: "steps.demoPerformance.message",
    next: "steps.demoPerformance.next",
  },
  demoCoursework: {
    navigationTarget: '[data-demo-nav="records-coursework"]',
    target: '[data-demo-tour-panel="records"]',
    title: "steps.demoCoursework.title",
    message: "steps.demoCoursework.message",
    next: "steps.demoCoursework.next",
  },
  demoGroups: {
    navigationTarget: '[data-demo-nav="groups"]',
    target: '[data-demo-tour-panel="groups"]',
    title: "steps.demoGroups.title",
    message: "steps.demoGroups.message",
    next: "steps.demoGroups.next",
  },
  demoSeating: {
    navigationTarget: '[data-demo-nav="seating"]',
    target: '[data-demo-tour-panel="seating"]',
    title: "steps.demoSeating.title",
    message: "steps.demoSeating.message",
    next: "steps.demoSeating.next",
  },
  demoCalendar: {
    navigationTarget: '[data-demo-nav="calendar"]',
    target: '[data-demo-tour-panel="calendar"]',
    title: "steps.demoCalendar.title",
    message: "steps.demoCalendar.message",
    next: "steps.demoCalendar.next",
  },
  demoAttendance: {
    navigationTarget: '[data-demo-nav="attendance"]',
    target: '[data-demo-tour-panel="attendance"]',
    title: "steps.demoAttendance.title",
    message: "steps.demoAttendance.message",
    next: "steps.demoAttendance.next",
  },
  demoHomeroomHomework: {
    navigationTarget: '[data-demo-nav="homeroomHomework"]',
    target: '[data-demo-tour-panel="homeroomHomework"]',
    title: "steps.demoHomeroomHomework.title",
    message: "steps.demoHomeroomHomework.message",
    next: "steps.demoHomeroomHomework.next",
  },
  demoStudentRecords: {
    navigationTarget: '[data-demo-nav="studentRecords"]',
    target: '[data-demo-tour-panel="studentRecords"]',
    title: "steps.demoStudentRecords.title",
    message: "steps.demoStudentRecords.message",
    next: "steps.demoStudentRecords.next",
  },
  demoHomeroom: {
    navigationTarget: '[data-demo-nav="homeroom"]',
    target: '[data-demo-tour-panel="homeroom"]',
    title: "steps.demoHomeroom.title",
    message: "steps.demoHomeroom.message",
    next: "steps.demoHomeroom.next",
  },
  demoHomeroomDuties: {
    navigationTarget: '[data-demo-nav="homeroomDuties"]',
    target: '[data-demo-tour-panel="homeroomDuties"]',
    title: "steps.demoHomeroomDuties.title",
    message: "steps.demoHomeroomDuties.message",
    next: "steps.demoHomeroomDuties.next",
  },
  demoToolSettings: {
    navigationTarget: '[data-demo-nav="roster"]',
    target: '[data-demo-tour-panel="roster"]',
    title: "steps.demoToolSettings.title",
    message: "steps.demoToolSettings.message",
    next: "steps.demoToolSettings.next",
  },
  demoRoster: {
    navigationTarget: '[data-demo-nav="roster"]',
    target: '[data-demo-tour-panel="roster"]',
    title: "steps.demoRoster.title",
    message: "steps.demoRoster.message",
    next: "steps.demoRoster.next",
  },
  demoData: {
    navigationTarget: '[data-demo-nav="billing"]',
    target: '[data-demo-tour-panel="billing"]',
    title: "steps.demoData.title",
    message: "steps.demoData.message",
    next: "steps.demoData.next",
  },
};

export default function GuidedTour({
  step,
  variant = "setup",
  continueSetup = false,
  onNext,
  onBack,
  onSkip,
}: {
  step: TourStep | DemoTourStep;
  variant?: "setup" | "demo";
  continueSetup?: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation("guidedTour");
  const maskId = useId();
  const [navigationRect, setNavigationRect] = useState<DOMRect | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [ready, setReady] = useState(false);
  const demoIndex = variant === "demo" ? DEMO_TOUR_SEQUENCE.indexOf(step as DemoTourStep) : -1;
  const item: TourItem = useMemo(() => {
    if (variant === "demo") return { ...demoSteps[step as DemoTourStep], chapter: demoIndex + 1 };
    const setupItem = steps[step as TourStep];
    // Keep the class selector usable before entering the roster or seating tool.
    return continueSetup && (step === "rosterLink" || step === "seatingLink")
      ? { ...setupItem, target: '[data-tour="roster-seating-setup"]' }
      : setupItem;
  }, [variant, step, demoIndex, continueSetup]);
  useEffect(() => {
    let frame = 0;
    let target: HTMLElement | null = null;
    let navigationTarget: HTMLElement | null = null;
    const measure = () => {
      const next = document.querySelector<HTMLElement>(item.target);
      if (next !== target) {
        target = next;
        if (target) {
          target.scrollIntoView({
            block: variant === "demo" ? "start" : "center",
            inline: "nearest",
            behavior: "auto",
          });
          target.focus({ preventScroll: true });
        }
      }
      const nextNavigation = item.navigationTarget ? document.querySelector<HTMLElement>(item.navigationTarget) : null;
      if (nextNavigation !== navigationTarget) {
        navigationTarget = nextNavigation;
        navigationTarget?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
      }
      const navigationBounds = navigationTarget?.getBoundingClientRect();
      setNavigationRect(navigationBounds?.width && navigationBounds.height ? navigationBounds : null);
      setRect(target?.getBoundingClientRect() || null);
      setReady(!item.needsValue || Boolean((target as HTMLInputElement | HTMLTextAreaElement | null)?.value.trim()));
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    schedule();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    document.addEventListener("input", schedule, true);
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onSkip();
    };
    window.addEventListener("keydown", keydown);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
      document.removeEventListener("input", schedule, true);
      window.removeEventListener("keydown", keydown);
    };
  }, [item, onSkip, variant]);

  const width = window.innerWidth;
  const height = window.innerHeight;
  const left = rect ? Math.max(0, rect.left - 8) : 0;
  const top = rect ? Math.max(0, rect.top - 8) : 0;
  const right = rect ? Math.min(width, rect.right + 8) : 0;
  const bottom = rect ? Math.min(height, rect.bottom + 8) : 0;
  const focusAreas = [rect, navigationRect]
    .filter((bounds): bounds is DOMRect => bounds !== null)
    .map((bounds) => {
      const x = Math.max(0, Math.min(width, bounds.left - 8));
      const y = Math.max(0, Math.min(height, bounds.top - 8));
      return {
        x,
        y,
        width: Math.max(0, Math.min(width, bounds.right + 8) - x),
        height: Math.max(0, Math.min(height, bounds.bottom + 8) - y),
      };
    });
  const cardWidth = Math.min(350, width - 24);
  const isFeatureIntro = variant === "demo" && step !== "demoWelcome";
  const cardLeft = isFeatureIntro
    ? Math.max(12, width - cardWidth - 24)
    : rect
      ? Math.max(12, Math.min(width - cardWidth - 12, left))
      : Math.max(12, (width - cardWidth) / 2);
  const cardTop = isFeatureIntro
    ? Math.max(12, height - 250)
    : rect
      ? bottom + 330 < height
        ? bottom + 17
        : Math.max(12, top - 320)
      : Math.max(12, (height - 270) / 2);

  return createPortal(
    <section className="guided-tour" aria-label={t(variant === "demo" ? "regions.demo" : "regions.setup")}>
      {variant === "demo" && (
        <>
          <svg className="tour-shade-mask" width={width} height={height} aria-hidden="true">
            <defs>
              <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width={width} height={height}>
                <rect width={width} height={height} fill="white" />
                {focusAreas.map((area, index) => (
                  <rect key={index} {...area} fill="black" />
                ))}
              </mask>
            </defs>
            <rect width={width} height={height} fill="rgba(9, 24, 20, 0.72)" mask={`url(#${maskId})`} />
          </svg>
          {focusAreas.map((area, index) => (
            <div
              key={index}
              className="tour-spotlight"
              data-testid={index === 0 ? "tour-content-spotlight" : "demo-nav-spotlight"}
              style={{ left: area.x, top: area.y, width: area.width, height: area.height }}
              aria-hidden="true"
            />
          ))}
        </>
      )}
      {variant !== "demo" && rect && (
        <>
          <div className="tour-shade" style={{ left: 0, top: 0, width, height: top }} />
          <div className="tour-shade" style={{ left: 0, top, width: left, height: bottom - top }} />
          <div className="tour-shade" style={{ left: right, top, width: width - right, height: bottom - top }} />
          <div className="tour-shade" style={{ left: 0, top: bottom, width, height: height - bottom }} />
          <div
            className="tour-spotlight"
            style={{ left, top, width: right - left, height: bottom - top }}
            aria-hidden="true"
          />
        </>
      )}
      {variant !== "demo" && !rect && <div className="tour-shade" style={{ inset: 0 }} />}
      <section
        className="tour-dialog"
        role="dialog"
        aria-modal="false"
        aria-labelledby="tour-title"
        aria-describedby="tour-description"
        style={{ left: cardLeft, top: cardTop, width: cardWidth }}
      >
        <div className="tour-dialog-head">
          <span className="tour-mascot" aria-hidden="true">
            {t("mascot")}
          </span>
          <div>
            <small>
              {t(variant === "demo" ? "progress.demo" : "progress.setup", {
                chapter: continueSetup ? item.chapter - 1 : item.chapter,
                total: variant === "demo" ? DEMO_TOUR_SEQUENCE.length : continueSetup ? 2 : 3,
              })}
            </small>
            <h2 id="tour-title">{t(item.title)}</h2>
          </div>
        </div>
        <p id="tour-description">
          {rect || step === "seatingNav" || variant === "demo" ? t(item.message) : t("preparing")}
        </p>
        <div className="tour-dialog-actions">
          <button type="button" className="tour-skip" onClick={() => onSkip()}>
            {t(variant === "demo" ? "actions.close" : continueSetup ? "actions.manualSetup" : "actions.skip")}
          </button>
          {((variant === "demo" && demoIndex > 0) || step === "rosterSubmit") && (
            <button type="button" className="tour-back" onClick={onBack}>
              {t(step === "rosterSubmit" ? "actions.editRoster" : "actions.back")}
            </button>
          )}
          {item.next && (
            <button
              type="button"
              className="tour-next"
              onClick={onNext}
              disabled={variant !== "demo" && ((!rect && step !== "seatingNav") || !ready)}
            >
              {t(item.next)}
            </button>
          )}
        </div>
      </section>
    </section>,
    document.body,
  );
}
